import { errorWords } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "@/lib/icons";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { SharedRowNote } from "@/components/app/lists/shared-row-note";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useCanOrganizationWide, useCanWithoutBuilding } from "@/lib/policy";
import { CourseTable } from "./-components/course-table";
import type { CourseRow } from "./-components/course-form";

export const Route = createFileRoute("/_authenticated/_workforce/training/courses/")({
  component: TrainingCourses,
});

/** Everything the building can assign, and what state each course is in. */
function TrainingCourses() {
  // Every building shares a course, so changing one takes training:manage for the organization.
  const canManage = useCanOrganizationWide("training:manage");
  const managesSomewhere = useCanWithoutBuilding("training:manage");
  const viewOnly = managesSomewhere && !canManage;
  const queryClient = useQueryClient();
  const [archiving, setArchiving] = useState<CourseRow | null>(null);

  const courses = useQuery(orpc.workforce.training.listCourses.queryOptions({ input: {} }));

  const archive = useMutation(
    orpc.workforce.training.archiveCourse.mutationOptions({
      onSuccess: async () => {
        toast.success("Course archived. Nobody new can be assigned it");
        setArchiving(null);
        await queryClient.invalidateQueries({ queryKey: orpc.workforce.training.key() });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const rows = courses.data ?? [];

  return (
    <>
      <PageHeader
        title="Courses"
        count={courses.data ? rows.length : undefined}
        meta="What the building can assign, and whether each one is finished enough to give somebody."
        actions={
          canManage ? (
            <Button asChild>
              <Link to="/training/courses/new">
                <Plus className="size-4" />
                New course
              </Link>
            </Button>
          ) : viewOnly ? (
            <SharedRowNote />
          ) : null
        }
      />

      {courses.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : courses.isError ? (
        <ReadFailed
          message="Could not load the courses."
          error={courses.error}
          onRetry={() => void courses.refetch()}
          retrying={courses.isFetching}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No courses yet"
          description="A course is a set of sections and, if you want one, a quiz. Courses you build appear here and can be assigned."
        />
      ) : (
        <CourseTable
          rows={rows}
          canManage={canManage}
          viewOnly={viewOnly}
          onArchive={setArchiving}
        />
      )}

      <AlertDialog open={archiving !== null} onOpenChange={(open) => !open && setArchiving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive “{archiving?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Nobody new can be assigned it. Anybody part-way through keeps their progress and their
              certificate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep course</AlertDialogCancel>
            <AlertDialogAction
              disabled={archive.isPending}
              onClick={() => archiving && archive.mutate({ courseId: archiving.id })}
            >
              Archive course
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
